import { Box, Icon } from "@outschool/backpack";
import { faEnvelope } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import {
  addParamsToUrl,
  joinParentAccountPath,
  joinTeacherAccountPath,
  loginPath,
  teachPath
} from "@outschool/routes";
import { logException } from "@outschool/sentry/dist/client";
import { useAnalytics } from "@outschool/ui-analytics";
import {
  ContinueWithApple,
  ContinueWithFacebook,
  ContinueWithGoogle,
  LoginUserFeedback,
  getPostLoginPath,
  useAuthError
} from "@outschool/ui-auth";
import {
  AccountDisclaimer,
  TrackedButton
} from "@outschool/ui-components-shared";
import React from "react";
import { useHistory } from "react-router";

import {
  APPLE_SIGNIN_CLIENT_ID,
  FACEBOOK_APP_ID,
  FEATURE_FLAGS,
  GOOGLE_CLIENT_ID
} from "../../shared/Env";
import ActionType from "../actions/ActionType";
import {
  useLoginWithApple,
  useLoginWithRedirect
} from "../hooks/Authentication";
import { useAppState } from "../stores/AppStateProvider";
import Link from "./Link";

interface SignupFormProps {
  onRedirect?: (e: MouseEvent) => void;
  promptLogin?: boolean;
  isTeacherFlow?: boolean;
  trackingParameters?: { [key: string]: string };
  trackingUniqueId?: string;
  signupParams?: { [key: string]: string | boolean };
}

export default function SignupForm({
  onRedirect,
  promptLogin,
  isTeacherFlow,
  trackingParameters,
  trackingUniqueId,
  signupParams
}: SignupFormProps) {
  const postLoginPath = getPostLoginPath();
  const { t } = useTranslation("client\\components\\SignupForm");
  return (
    <Box
      sx={{
        display: "grid",
        gap: "1em",
        width: "100%"
      }}
    >
      <SignupButtons
        isTeacherFlow={isTeacherFlow}
        trackingParameters={trackingParameters}
        trackingUniqueId={trackingUniqueId}
        onRedirect={onRedirect}
        signupParams={signupParams}
        postLoginPath={postLoginPath}
      />
      {promptLogin ? (
        <Trans t={t}>
          <Box
            sx={{
              justifySelf: "center"
            }}
          >
            Already have an account?{" "}
            <Link
              data-test-id="login-link"
              onClick={onRedirect}
              to={addParamsToUrl(loginPath(), { postLoginPath })}
              trackingName="login"
              trackingUniqueId={trackingUniqueId}
            >
              Log in
            </Link>
          </Box>
        </Trans>
      ) : null}
      <AccountDisclaimer isSignup />
    </Box>
  );
}

function SignupButtons({
  isTeacherFlow,
  trackingParameters,
  trackingUniqueId,
  onRedirect,
  signupParams,
  postLoginPath
}) {
  const analytics = useAnalytics();
  const { t } = useTranslation("client\\components\\SignupForm");
  const history = useHistory();
  const [userFeedbackMessage, setUserFeedbackMessage] =
    React.useState<React.ReactNode>(null);
  const appState = useAppState();

  const createParams = {
    isGiftCardSignup: signupParams?.isGiftCardSignup,
    isLeader: isTeacherFlow,
    trackingParameters
  };

  const defaultErrorMessage = t("Sorry that didn't work, try again");
  const handleAuthError = React.useCallback(
    (error, message: React.ReactNode = defaultErrorMessage) => {
      logException(error, {
        tags: { component: Component.UserAccountManagement }
      });
      setUserFeedbackMessage(message);
    },
    [defaultErrorMessage]
  );

  const generateLoginError = useAuthError();

  const handleLoginSuccess = React.useCallback(
    ({ sessionToken, refreshToken, postLoginPath, isLearnerTransfer }) => {
      if (!isLearnerTransfer) {
        appState.appDispatcher.dispatch(ActionType.User.LOGGED_IN, {
          sessionToken,
          refreshToken
        });
        const redirectPath = isTeacherFlow ? teachPath() : postLoginPath;
        history.push(redirectPath);
      } else {
        appState.appDispatcher.dispatch(
          ActionType.User.REDIRECT_TO_LEARNER_APP,
          {
            transferToken: sessionToken,
            isLearnerLogin: true
          }
        );
      }
    },
    [history, appState.appDispatcher, isTeacherFlow]
  );

  const handleLoginError = React.useCallback(
    error => {
      const errorObject = new Error(error);
      handleAuthError(errorObject, generateLoginError(error));
    },
    [handleAuthError, generateLoginError]
  );

  const handleAppleLogin = useLoginWithApple(
    handleLoginSuccess,
    handleLoginError,
    createParams,
    postLoginPath
  );

  const setAuthStrategy = useLoginWithRedirect(
    handleLoginError,
    createParams,
    // @ts-ignore TS(2345): Argument of type 'null' is not assignable to param... Remove this comment to see the full error message
    null,
    postLoginPath
  );

  return (
    <React.Fragment>
      <LoginUserFeedback
        // @ts-ignore TS(2322): Type 'null' is not assignable to type 'String | un... Remove this comment to see the full error message
        userFeedbackMessage={userFeedbackMessage}
        isError={true}
      />
      <ContinueWithGoogle
        trackingName="signup-with-google"
        setAuthStrategy={setAuthStrategy}
        // @ts-ignore TS(2322): Type '() => Promise<string | undefined>' is not as... Remove this comment to see the full error message
        getAnonymousId={analytics.anonymousId}
        clientId={GOOGLE_CLIENT_ID!}
      />
      {FEATURE_FLAGS.FACEBOOK_LOGIN_ENABLED && (
        <ContinueWithFacebook
          setAuthStrategy={setAuthStrategy}
          trackingName="signup-with-facebook"
          // @ts-ignore TS(2322): Type '() => Promise<string | undefined>' is not as... Remove this comment to see the full error message
          getAnonymousId={analytics.anonymousId}
          clientId={FACEBOOK_APP_ID!}
        />
      )}
      <ContinueWithApple
        onSuccess={handleAppleLogin}
        onError={handleAuthError}
        trackingName="signup-with-apple"
        clientId={APPLE_SIGNIN_CLIENT_ID!}
        logException={logException}
      />
      <TrackedButton
        component={Link}
        onClick={onRedirect}
        data-test-id="signup-flow-link"
        trackingName="signup-with-email"
        trackingUniqueId={trackingUniqueId}
        to={
          isTeacherFlow
            ? joinTeacherAccountPath()
            : joinParentAccountPath({ ...signupParams, postLoginPath })
        }
        sx={{
          padding: "0 !important",
          display: "flex",
          alignItems: "center"
        }}
      >
        <Box
          sx={{
            fontSize: "1.5em",
            padding: "10px",
            width: "44px"
          }}
        >
          <Icon
            icon={faEnvelope}
            sx={{
              display: "block"
            }}
          />
        </Box>
        <Box
          flex
          sx={{
            flex: 1,
            padding: "12.5px",
            minWidth: "215px",
            justifyContent: "center"
          }}
        >
          {t`Sign Up with Email`}
        </Box>
      </TrackedButton>
    </React.Fragment>
  );
}
